/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiType } from '@squiz/dxp-ai-types';
import { PERMISSIONS } from '@squiz/dxp-auth';
import { Breadcrumbs, BreadcrumbsProps } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { LoadingScreenEmpty } from 'apps/console-core/src/Screens/LoadingScreenEmpty/LoadingScreenEmpty';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from '../../../../console-core/src/Utilities/HasPermission/HasPermission';
import { AiContextTable } from '../../Components/AiContextTable/AiContextTable';
import { AiDetailsCard } from '../../Components/AiDetailsCard/AiDetailsCard';
import { AiInstructionCard } from '../../Components/AiInstructionCard/AiInstructionCard';
import { AiThreadTable } from '../../Components/AiThreadTable/AiThreadTable';
import { EditToolDialog } from '../../Components/EditToolDialog/EditToolDialog';
import { TestToolDialog } from '../../Components/TestToolDialog/TestToolDialog';
import { ViewAiMoreDropdown } from '../../Components/ViewAiMoreDropdown/ViewAiMoreDropdown';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { useGetAi } from '../../Services/ai';

/**
 * The view AI chatbot screen.
 */

export const AiViewChatbot = (): JSX.Element => {
  const navigate = useNavigate();

  const screenTitle = ''; // Exclude heading from doc title
  let screenContent;
  let screenHeading = '';

  const params = useParams();
  const { contentToolId } = params;
  const [cacheKey, setCacheKey] = useState<string>(``);
  const [contentTool, error, contentToolState] = useGetAi(contentToolId ?? '', 'chatbot', cacheKey);
  const { t } = useTranslation();
  const labels = {
    aiChatbots: t(AI_STRINGS.aiChatbots),
    contexts: t(AI_STRINGS.contexts),
    contextsDescription: t(AI_STRINGS.contextsDescription),
    contextsEmpty: t(AI_STRINGS.contextsEmpty),
    conversationHistoryEmpty: t(AI_STRINGS.conversationsHistoryEmpty),
    conversationHistoryTableDescription: t(AI_STRINGS.conversationsHistoryTableDescription),
    conversationHistoryTableHeading: t(AI_STRINGS.conversationsHistoryTableHeading),
  };

  // Breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.aiChatbots,
        url: getCapabilityPath('/ai-chatbots'),
      },
    ],
  };

  if (contentToolState === 'error' && error) {
    navigate({ pathname: '*' });
  }

  if (contentToolState === 'pending') {
    return <LoadingScreenEmpty></LoadingScreenEmpty>;
  }

  if (contentToolState === 'complete' && contentTool) {
    screenHeading = contentTool.name;
    breadcrumbProps.currentPageName = contentTool.name;
    screenContent = (
      <div className="view-chat-bot">
        <div className="view-chat-bot__action-buttons">
          <TestToolDialog
            contentTool={contentTool}
            editButton={
              <EditToolDialog clearCache={setCacheKey} contentTool={contentTool} title={''} type={AiType.chatbot} />
            }
            title={`Test chatbot "${contentTool.name}"`}
          />
          <EditToolDialog clearCache={setCacheKey} contentTool={contentTool} title={''} type={AiType.chatbot} />
          <ViewAiMoreDropdown aiId={contentToolId} type={AiType.chatbot} />
        </div>
        <div className="view-chat-bot__card-container">
          <AiInstructionCard ai={contentTool} />
          <AiDetailsCard ai={contentTool} />
        </div>
        {hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_READ) && (
          <AiContextTable
            ai={contentTool}
            labels={{
              description: labels.contextsDescription,
              emptyText: labels.contextsEmpty,
              heading: labels.contexts,
            }}
          />
        )}
        {hasPermission(PERMISSIONS.dxpAi.CHATBOT_THREAD_READ) && (
          <AiThreadTable
            aiId={contentTool.id}
            description={labels.conversationHistoryTableDescription}
            emptyText={labels.conversationHistoryEmpty}
            heading={labels.conversationHistoryTableHeading}
            type={contentTool.type}
          />
        )}
      </div>
    );
  }

  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="view-ai-chatbot-breadcrumbs" />;

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading} showSeparator title={screenTitle}>
      {screenContent}
    </DxpScreen>
  );
};
